/**
 * EcommercePage - Page principale e-commerce avec layout 70/30
 * NOUVELLE VERSION avec navigation par catégories comme le POS
 * Intègre le Live Shopping avec synchronisation panier
 */

import { adresseProduit } from '../../utils/adresseProduit';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ficheOuverte, rechercheEnCours, positionListe, vueCourante } from '../../utils/repriseBoutique';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
// Video, Phone, MessageCircle — désactivés temporairement (Live Shopping masqué)
// import { Video, Phone, MessageCircle } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import EcommerceHeader from '../../components/ecommerce/layout/EcommerceHeader';
import EcommerceFooter from '../../components/ecommerce/layout/EcommerceFooter';
import BarreAideBoutique from '../../components/ecommerce/BarreAideBoutique';
import { useShopOrdering } from '../../hooks/ecommerce/useShopOrdering';
import CategorySidebar from '../../components/ecommerce/products/CategorySidebar';
import { useFavoris } from '../../hooks/ecommerce/useFavoris';
import BarreFavoris from '../../components/ecommerce/products/BarreFavoris';
import MobileCategoryChips from '../../components/ecommerce/layout/MobileCategoryChips';
import MobileBottomNav from '../../components/ecommerce/layout/MobileBottomNav';
import InstallPrompt from '../../components/ecommerce/layout/InstallPrompt';
import OfflineBanner from '../../components/ecommerce/layout/OfflineBanner';
import ProductCard from '../../components/ecommerce/products/ProductCard';
import ProductDetailModal from '../../components/ecommerce/products/ProductDetailModal';
import ProductSkeleton from '../../components/ecommerce/products/ProductSkeleton';
import ReceiptTicket from '../../components/ecommerce/cart/ReceiptTicket';
// @ts-ignore — Live Shopping désactivé temporairement, imports conservés
import WaitingRoom from '../../components/live-shopping/WaitingRoom';
// @ts-ignore
import LiveVideoPanel from '../../components/live-shopping/LiveVideoPanel';
// @ts-ignore
import ChatOnlyPanel from '../../components/live-shopping/ChatOnlyPanel';
import { useLiveSession, type EcommerceCartAction } from '../../hooks/live-shopping';
import { useCustomer } from '../../contexts/CustomerContext';
import { useEcommerceCart } from '../../hooks/ecommerce/useEcommerceCart';
import { useIsMobile } from '../../hooks/useIsMobile';
import AccueilBoutique, { accueilMasque, reafficherAccueil } from '../../components/ecommerce/AccueilBoutique';
import { ecommerceApi } from '../../api/ecommerce.api';
import type { Product, Category } from '../../types/ecommerce.types';
import { logger } from '../../utils/logger';
import { searchProducts } from '../../utils/productSearch';
import { useTitrePage } from '../../hooks/useTitrePage';
import { useCanonical, cheminCatalogue } from '../../hooks/useCanonical';

export default function EcommercePage() {
  const isMobile = useIsMobile();
  // Bannière d'accueil (photo de la ferme, fonctionnement, pain) : masquable 30 j.
  const [accueilCache, setAccueilCache] = useState(() => accueilMasque());
  const debutGrilleRef = useRef<HTMLDivElement>(null);
  const { customer, isAuthenticated: customerAuthenticated } = useCustomer();

  // L'accès pré-lancement est contrôlé par ShopGate (code partagé) au niveau
  // des routes /shop — plus de barrière staff ici
  const isAuthenticated = customerAuthenticated;

  // E-commerce cart hook
  const { items: cartItems, addItem, removeItem, updateQuantity } = useEcommerceCart();

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const ordering = useShopOrdering();
  const [loading, setLoading] = useState(true);
  // Le catalogue n'a pas pu être chargé (hors ligne, réseau coupé…) : on le dit
  // et on réessaie seul (14/10/2026 — ouverte hors ligne depuis l'accueil, la
  // boutique restait vide jusqu'à fermer l'application).
  const [catalogueEnEchec, setCatalogueEnEchec] = useState(false);
  const [nouvelEssai, setNouvelEssai] = useState(false);
  // Recherche, fiche ouverte et position survivent à un rechargement de
  // l'onglet : voir utils/repriseBoutique.ts (Android qui vide l'onglet).
  const [searchQuery, setSearchQuery] = useState(() => rechercheEnCours.lire());
  const [ownProductionOnly, setOwnProductionOnly] = useState(false);
  // Anti-gaspi : produits en promo DLC courte (-50 %) — filtre transversal
  const [promoOnly, setPromoOnly] = useState(false);
  // Nouveautés annoncées cette semaine — même famille de filtre transversal.
  const [noveltyOnly, setNoveltyOnly] = useState(false);
  // Favoris : liste composee par le client lui-meme (voir useFavoris).
  const { estFavori, basculer: basculerFavori, favoris, actif: favorisActifs, connecte: favorisAuCompte } = useFavoris();
  const [favorisOnly, setFavorisOnly] = useState(false);
  const [sousRayonChoisi, setSelectedSubCategoryId] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('produit') ? null : ficheOuverte.lire(),
  );
  /**
   * Lien profond `/shop?produit=<id>` : c'est le chaînon qui manquait partout.
   * Sans lui, un produit nommé dans une actualité ou dans un email n'était
   * cliquable vers rien — la boutique n'avait aucune adresse pour un produit.
   * La fiche s'ouvre directement, et le paramètre est retiré aussitôt pour que
   * la fermer ne la rouvre pas au rendu suivant.
   */
  const [searchParams, setSearchParams] = useSearchParams();

  // Entree depuis « Mon compte » : /shop?favoris=1. Le parametre est retire
  // aussitot, comme le lien profond `?produit=`, pour que fermer le filtre ne
  // le rouvre pas au rendu suivant.
  useEffect(() => {
    if (searchParams.get('favoris') !== '1') return;
    setFavorisOnly(true);
    const p = new URLSearchParams(searchParams);
    p.delete('favoris');
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * Demande de retour à l'accueil, venue du logo du bandeau. On remet la
   * boutique dans l'état d'un premier chargement : plus de rayon, plus de
   * filtre, plus de recherche, et retour en haut de la liste. Sans ça, cliquer
   * le logo depuis un rayon ne changeait rien à l'écran.
   */
  const location = useLocation();
  const zoneProduitsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const demande = (location.state as { accueil?: number } | null)?.accueil;
    if (!demande) return;
    setSelectedCategoryId(null);
    setSelectedSubCategoryId(null);
    setOwnProductionOnly(false);
    setPromoOnly(false);
    setFavorisOnly(false);
    setNoveltyOnly(false);
    setSearchQuery('');
    setDisplayCount(TRANCHE_CATALOGUE);
    zoneProduitsRef.current?.scrollTo({ top: 0 });
  }, [location.state]);
  // Une seule direction : l'état est écrit dans l'onglet, jamais l'inverse
  // après le premier rendu.
  useEffect(() => {
    ficheOuverte.ecrire(selectedProductId);
  }, [selectedProductId]);
  useEffect(() => {
    rechercheEnCours.ecrire(searchQuery);
  }, [searchQuery]);
  // Quitter la boutique (panier, compte) ferme la fiche pour de bon : seul un
  // RECHARGEMENT, qui ne démonte rien, doit la rouvrir.
  useEffect(() => () => ficheOuverte.ecrire(null), []);

  // `?produit=` accepte l'identifiant OU le code de la fiche (`?produit=BAC-FOLIES`) :
  // un texte de la boutique peut ainsi viser une fiche sans écrire l'identifiant
  // d'une base précise (lien « bac bleu » du panier, 17/10/2026).
  const [produitParCode, setProduitParCode] = useState<string | null>(null);
  useEffect(() => {
    const produit = searchParams.get('produit');
    if (!produit) return;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(produit)) setSelectedProductId(produit);
    else setProduitParCode(produit);
    const reste = new URLSearchParams(searchParams);
    reste.delete('produit');
    setSearchParams(reste, { replace: true });
  }, [searchParams, setSearchParams]);
  useEffect(() => {
    if (!produitParCode || products.length === 0) return;
    const fiche = products.find((p: any) => p.code === produitParCode);
    if (fiche) setSelectedProductId(fiche.id);
    setProduitParCode(null);
  }, [produitParCode, products]);

  /**
   * Lien profond `/shop?rayon=<slug>` — l'adresse d'un RAYON.
   *
   * Contrairement à `?produit=`, ce paramètre n'est PAS retiré : il décrit ce
   * qu'on regarde. Une vue de rayon devient partageable et remise en signet —
   * alors que toute la boutique n'avait jusqu'ici qu'UNE adresse, `/shop`. Le
   * changement de rayon s'écrit en `replace`, donc il ne construit PAS
   * d'historique : « Précédent » quitte le catalogue, il ne revient pas au
   * rayon d'avant — exactement comme avant ce chantier.
   *
   * Motif : la reprise de foliesmaraicheres.be (hébergement clos en octobre
   * 2026). Sur ses 143 anciennes fiches indexées, 78 n'ont plus d'équivalent
   * exact — surtout des fruits et légumes de saison. Sans adresse de rayon,
   * leur redirection ne pouvait viser que le catalogue entier ; avec elle, un
   * visiteur venu chercher des asperges arrive au moins dans les légumes.
   *
   * ⚠️ **Le rayon n'a PAS d'état local : l'adresse EST l'état.** Une première
   * version gardait un `useState` synchronisé avec l'URL par deux effets, l'un
   * lisant, l'autre écrivant, chacun gardé par « n'agir que si les deux
   * diffèrent ». Ce raisonnement est faux : les deux effets tournent dans la
   * MÊME purge, donc le second lit encore la valeur du rendu courant, que le
   * premier vient seulement de demander à changer. Les deux valeurs échangent
   * alors leur place à chaque tour sans jamais converger — mesuré à 996
   * réécritures d'adresse sur un simple retour navigateur, sur la page d'entrée
   * de la boutique. Le même décalage faisait rétablir le rayon juste après que
   * le clic sur le logo l'ait remis à zéro, annulant en silence le correctif du
   * 10/09/2026. Une seule source de vérité supprime les deux défauts d'un coup.
   */
  const rayonDemande = searchParams.get('rayon');
  // Titre et adresse canonique suivent ce qu'on regarde : une fiche produit
  // ouverte porte son nom et sa propre adresse (indexation Google, 12/10/2026).
  const produitOuvert = selectedProductId ? products.find((p) => p.id === selectedProductId) : null;
  useTitrePage(produitOuvert?.name ?? 'Catalogue');
  // Fiche ouverte en fenêtre : l'adresse officielle est sa page propre (18/10/2026).
  useCanonical(produitOuvert ? adresseProduit(produitOuvert) : cheminCatalogue(selectedProductId, rayonDemande));

  const selectedCategoryId = useMemo(
    () => (rayonDemande ? categories.find((c) => c.slug === rayonDemande)?.id ?? null : null),
    [categories, rayonDemande],
  );

  /**
   * Même signature que l'ancien setter d'état : tous les appelants (barre
   * latérale, puces mobiles, fil d'Ariane) restent inchangés.
   *
   * La forme fonctionnelle de `setSearchParams` est utilisée pour la lisibilité,
   * mais elle ne garantit RIEN sur la fraîcheur : react-router 6.30.1 lui passe
   * le `searchParams` du rendu, pas les paramètres courants. Ce qui rend la
   * lecture juste ici, c'est que le rappel est recréé à chaque changement de
   * `searchParams` et qu'un seul écrivain touche `rayon`. Enchaîner deux appels
   * dans le même tour perdrait le premier.
   */
  const setSelectedCategoryId = useCallback(
    (categoryId: string | null) => {
      const slug = categoryId ? categories.find((c) => c.id === categoryId)?.slug ?? null : null;
      setSearchParams(
        (precedents) => {
          const p = new URLSearchParams(precedents);
          if (p.get('rayon') === slug) return p; // rien à changer
          if (slug) p.set('rayon', slug);
          else p.delete('rayon');
          return p;
        },
        { replace: true },
      );
    },
    [categories, setSearchParams],
  );
  // Par défaut : les plus vendus en caisse d'abord (Philippe, 28/09/2026).
  const [sortBy, setSortBy] = useState<string>('best');
  // Taille d'une tranche du catalogue. Elle a longtemps été basse (40) parce que
  // chaque vignette téléchargeait la photo PLEINE — 162 Ko en moyenne, soit
  // 6,3 Mo pour une seule tranche. Depuis que les vignettes servent la miniature
  // (≈15 Ko) et ne se chargent qu'à l'approche de l'écran, la contrainte a
  // disparu : on en affiche davantage, et la suite arrive au défilement.
  const TRANCHE_CATALOGUE = 60;
  const [displayCount, setDisplayCount] = useState(TRANCHE_CATALOGUE);
  const sentinelleRef = useRef<HTMLDivElement | null>(null);

  // Live Shopping state (désactivé temporairement — variables conservées pour réactivation)
  const [, setShowWaitingRoom] = useState(false);
  const [, setActiveSessionId] = useState<string | null>(null);
  const [, setIsSessionHidden] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [, _setShowChatOnly] = useState(false); void _setShowChatOnly;
  const [, setVendorSessionEnded] = useState<{
    preparedItemsCount: number;
    totalPreparedCents: number;
    orderNumber?: string;
    message: string;
  } | null>(null);

  // Handler pour les actions panier du vendeur
  const handleCartAction = useCallback((action: EcommerceCartAction) => {
    logger.log('[EcommercePage] Applying cart action from vendor:', action);

    if (action.type === 'add' && action.product) {
      addItem(action.product as Product, { quantity: action.quantity || 1, addedBy: 'vendor' });
    } else if (action.type === 'remove' && action.productId) {
      removeItem(action.productId);
    } else if (action.type === 'update' && action.productId && action.quantity !== undefined) {
      updateQuantity(action.productId, action.quantity);
    }
  }, [addItem, removeItem, updateQuantity]);

  // Live session hook - centralisé pour maintenir la connexion
  const {
    connect: connectLive,
    availableVendors,
    joinSession: _joinSession,
    messages: _messages,
    sendMessage: _sendMessage,
    syncCartState,
    cancelSession: _cancelSession,
    isConnected: isLiveConnected,
    sessionId: hookSessionId,
    // Fonctions de gestion des frais (désactivées temporairement)
    getCancellationFees: _getCancellationFees,
    cancelSessionWithFees: _cancelSessionWithFees,
  } = useLiveSession({
    role: 'customer',
    customerId: customer?.id,
    onSessionAssigned: async (sessionId) => {
      logger.log('[EcommercePage] Session assigned:', sessionId);
      setShowWaitingRoom(false);
      setActiveSessionId(sessionId);
      setIsSessionHidden(false);
    },
    onCartAction: handleCartAction,
    onVendorJoined: () => {
      // Vendor joined - resync cart to ensure vendor has latest state
      logger.log('[EcommercePage] Vendor joined, resyncing cart:', cartItems.length, 'items');
      if (hookSessionId) {
        syncCartState(cartItems);
      }
    },
    onVendorSessionEnded: (data) => {
      // Le vendeur a terminé sa session - afficher le message au client
      logger.log('[EcommercePage] Vendor ended session:', data);
      setVendorSessionEnded(data);
      // Stocker le numéro de commande pré-réservé pour le checkout
      if (data.orderNumber) {
        sessionStorage.setItem('live_shopping_order_number', data.orderNumber);
        logger.log('[EcommercePage] Stored reserved order number:', data.orderNumber);
      }
      if (hookSessionId) {
        sessionStorage.setItem('live_shopping_session_id', hookSessionId);
      }
    },
  });

  // Sync cart state to vendor when cart changes and session is active
  useEffect(() => {
    if (hookSessionId && isLiveConnected && cartItems.length >= 0) {
      logger.log('[EcommercePage] Syncing cart to vendor:', cartItems.length, 'items');
      syncCartState(cartItems);
    }
  }, [hookSessionId, isLiveConnected, cartItems, syncCartState]);

  // Connecter au WebSocket live au montage
  useEffect(() => {
    connectLive().catch(console.error);
  }, [connectLive]);

  // Calculer disponibilité Live Shopping
  // Live Shopping — calculs conservés pour réactivation
  const _vendorCount = availableVendors.filter((v) => v.is_available && !v.is_in_session).length;
  void _vendorCount; void walletBalance; // suppress unused warnings

  // Charger tous les produits et catégories au montage
  useEffect(() => {
    loadData();
  }, []);

  // Catalogue en échec : nouvel essai toutes les 15 s, au retour du réseau et
  // quand l'application revient au premier plan. Pas seulement sur l'événement
  // « online » : navigator.onLine se trompe dans les deux sens sur ces appareils.
  useEffect(() => {
    if (!catalogueEnEchec) return;
    const reessayer = () => { if (document.visibilityState === 'visible') loadData(true); };
    const minuterie = window.setInterval(reessayer, 15000);
    window.addEventListener('online', reessayer);
    document.addEventListener('visibilitychange', reessayer);
    return () => {
      window.clearInterval(minuterie);
      window.removeEventListener('online', reessayer);
      document.removeEventListener('visibilitychange', reessayer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogueEnEchec]);

  // Charger le solde du wallet si connecté
  useEffect(() => {
    const loadWallet = async () => {
      if (isAuthenticated && customer) {
        try {
          const walletData = await ecommerceApi.wallet.getBalance();
          setWalletBalance(walletData.balance_cents);
        } catch (error) {
          logger.error('Erreur chargement wallet:', error);
          setWalletBalance(0);
        }
      } else {
        setWalletBalance(0);
      }
    };
    loadWallet();
  }, [isAuthenticated, customer]);

  // `silencieux` : nouvel essai après un échec — la page reste en place (le
  // chargement normal la remplace par des cases d'attente, ce qui clignoterait).
  const loadData = async (silencieux = false) => {
    try {
      if (silencieux) setNouvelEssai(true);
      else setLoading(true);
      const [productsData, categoriesData] = await Promise.all([
        ecommerceApi.products.getProducts(),
        ecommerceApi.products.getCategories(),
      ]);

      const productsArray = Array.isArray(productsData) ? productsData : [];
      const categoriesArray = Array.isArray(categoriesData) ? categoriesData : [];

      const productsWithPrice = productsArray.filter(
        (p: Product) => p.prices && p.prices.some((price) => price.currency === 'EUR')
      );

      setProducts(productsWithPrice);
      useEcommerceCart.getState().rafraichirConsignes(productsWithPrice);
      setCategories(categoriesArray);
      setCatalogueEnEchec(false);
    } catch (error) {
      logger.error('Erreur chargement données:', error);
      // Un échec ne vide pas un catalogue déjà affiché : il le laisse en place.
      setCatalogueEnEchec(true);
    } finally {
      if (silencieux) setNouvelEssai(false);
      else setLoading(false);
    }
  };

  // Chemin lisible d'une catégorie (« Légumes > Tomates ») pour la recherche
  const categoryPathById = useMemo(() => {
    const byId = new Map(categories.map((c) => [c.id, c]));
    const path = new Map<string, string>();
    for (const cat of categories) {
      const names: string[] = [];
      let cur: Category | undefined = cat;
      let guard = 0;
      while (cur && guard++ < 6) {
        names.unshift(cur.name);
        cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
      }
      path.set(cat.id, names.join(' > '));
    }
    return path;
  }, [categories]);

  // Fonction récursive pour obtenir tous les IDs de sous-catégories
  const getAllSubCategoryIds = (categoryId: string): string[] => {
    const subCategories = categories.filter((cat) => cat.parent_id === categoryId);
    let allIds = [categoryId];

    for (const subCat of subCategories) {
      allIds = [...allIds, ...getAllSubCategoryIds(subCat.id)];
    }

    return allIds;
  };

  /**
   * Un sous-rayon n'existe QUE sous son rayon.
   *
   * Tant que les deux étaient des `useState` voisins, l'incohérence était
   * impossible : leurs deux seuls appelants (barre latérale, puces mobiles) les
   * changent toujours ensemble. Depuis que le rayon vit dans l'adresse et pas
   * lui, n'importe quel `navigate('/shop')` nu efface le rayon et laisse le
   * sous-rayon derrière — l'onglet « Boutique » de la barre du bas, tapé alors
   * qu'il est déjà actif, ou la déconnexion. Le client se retrouvait alors dans
   * « Tomates » sans rayon, sur un catalogue filtré par un titre qu'aucun menu
   * ne montre plus.
   *
   * Plutôt qu'un effet qui remettrait le sous-rayon à zéro — la mécanique même
   * qui vient d'être retirée d'ici —, le sous-rayon est simplement IGNORÉ quand
   * il n'appartient pas au rayon courant. Une dérivation ne peut pas se
   * désaccorder de ce dont elle dérive.
   */
  const selectedSubCategoryId = useMemo(() => {
    if (!sousRayonChoisi || !selectedCategoryId) return null;
    return getAllSubCategoryIds(selectedCategoryId).includes(sousRayonChoisi) ? sousRayonChoisi : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sousRayonChoisi, selectedCategoryId, categories]);

  /**
   * La recherche porte sur TOUT le catalogue, quel que soit le rayon ouvert.
   * Remarque d'une cliente (25/09/2026) : dans « Légumes », « 5 céréales » ne
   * trouvait rien — la recherche était filtrée par le rayon, alors que la barre
   * est en haut de page et que le titre annonce « Recherche « … » ». Personne ne
   * pense à revenir à « Tous les produits » avant de chercher.
   *
   * Le rayon n'est pas effacé pour autant (il reste dans l'adresse) : il est
   * simplement IGNORÉ pendant la recherche, et retrouvé dès qu'on la vide.
   * Barre latérale, puces et fil d'Ariane lisent ces valeurs-ci, pour ne pas
   * montrer « Légumes » sélectionné au-dessus de flocons de céréales.
   */
  const enRecherche = Boolean(searchQuery.trim());
  const rayonAffiche = enRecherche ? null : selectedCategoryId;
  const sousRayonAffiche = enRecherche ? null : selectedSubCategoryId;

  /** Choisir un rayon pendant une recherche, c'est quitter la recherche. */
  const choisirRayon = useCallback(
    (categoryId: string | null) => {
      setSearchQuery('');
      setSelectedCategoryId(categoryId);
    },
    [setSelectedCategoryId],
  );
  const choisirSousRayon = useCallback((categoryId: string | null) => {
    setSearchQuery('');
    setSelectedSubCategoryId(categoryId);
  }, []);

  // Calcul des compteurs de produits par catégorie (tous niveaux)
  // Les compteurs de rayons suivent le filtre « notre production » : sans ça, un
  // rayon annoncerait 240 articles pour n'en afficher aucun une fois coché.
  const countableProducts = useMemo(() => {
    let base = products;
    if (ownProductionOnly) base = base.filter((p) => p.from_own_production);
    // « Anti-gaspi » : promo de fiche OU offre d'un lot remisé.
    if (promoOnly) base = base.filter((p) => p.promo_percent || p.lot_promos?.length);
    if (noveltyOnly) base = base.filter((p) => p.is_novelty);
    if (favorisOnly) base = base.filter((p) => favoris.includes(p.id));
    return base;
  }, [products, ownProductionOnly, promoOnly, noveltyOnly, favorisOnly, favoris]);

  const productCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    categories.forEach((cat) => {
      const allCategoryIds = getAllSubCategoryIds(cat.id);
      counts[cat.id] = countableProducts.filter((p) => allCategoryIds.includes(p.category_id || '')).length;
    });

    return counts;
  }, [countableProducts, categories]);

  // Pour le schéma « Quand commander ? » de l'entrée : quels rayons suivent la
  // fenêtre du frais, lesquels se commandent tous les jours. Un rayon qui ne
  // vend QUE des précommandes (la Boulangerie) n'est dans aucun des deux : il
  // a sa ligne à part, avec son heure limite.
  const rayonsParRythme = useMemo(() => {
    const ouverts = new Set(ordering?.always_open_category_ids || []);
    const fenetre: string[] = [];
    const toujours: string[] = [];
    for (const c of categories.filter((x) => !x.parent_id)) {
      const ids = getAllSubCategoryIds(c.id);
      const vendus = products.filter(
        (p) => ids.includes(p.category_id || '') && !p.preorder_enabled,
      );
      if (vendus.length === 0) continue;
      (ouverts.has(c.id) ? toujours : fenetre).push(c.name);
    }
    return { fenetre, toujours };
  }, [categories, products, ordering, getAllSubCategoryIds]);

  const ownProductionCount = useMemo(
    () => products.filter((p) => p.from_own_production).length,
    [products],
  );

  const promoCount = useMemo(
    () => products.filter((p) => p.promo_percent || p.lot_promos?.length).length,
    [products],
  );

  // Le serveur ne sert `is_novelty` que quand l'annonce est encore vraie : ce
  // compteur tombe donc à zéro tout seul, et la puce disparaît avec lui.
  const noveltyCount = useMemo(
    () => products.filter((p) => p.is_novelty).length,
    [products],
  );

  // Compte ce qui est REELLEMENT affichable : un favori retire du catalogue
  // (fiche desactivee, saison finie) ne doit pas gonfler un compteur qui
  // promettrait des articles introuvables.
  const favorisCount = useMemo(
    () => products.filter((p) => favoris.includes(p.id)).length,
    [products, favoris],
  );


  // Filtrage des produits affichés
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    if (searchQuery.trim()) {
      // Recherche tolérante par mots (accents, pluriels, début de mot, faute
      // de frappe), sur le nom, le rayon/la catégorie, la description, le
      // fournisseur… — classée par pertinence (voir utils/productSearch.ts).
      filtered = searchProducts(filtered, searchQuery, (p) => ({
        name: p.name,
        category: categoryPathById.get(p.category_id || '') || '',
        description: p.description,
        other: [p.code, p.barcode, (p as any).supplier_name, (p as any).producer_name]
          .filter(Boolean)
          .join(' '),
        rank: p.sales_rank ?? null,
      }));
    }

    if (ownProductionOnly) {
      filtered = filtered.filter((p) => p.from_own_production);
    }

    if (promoOnly) {
      filtered = filtered.filter((p) => p.promo_percent || p.lot_promos?.length);
    }

    if (noveltyOnly) {
      filtered = filtered.filter((p) => p.is_novelty);
    }

    // Un favori EPUISE reste dans la liste : une liste qui perd des articles
    // toute seule devient incomprehensible — la cliente croirait l'avoir
    // retire. Le catalogue masque deja les articles a zero (`in_stock_only`),
    // donc ce filtre montre ce qui est commandable parmi ses favoris.
    if (favorisOnly) {
      filtered = filtered.filter((p) => favoris.includes(p.id));
    }

    // Un sous-rayon peut avoir ses propres sous-catégories (« Pâtes, Riz &
    // Céréales » n'a AUCUN produit en direct, tout est dans ses 6 enfants) :
    // le filtre descend comme le compteur, sinon la pastille annonce 40
    // produits et la page répond « Aucun produit trouvé » (cliente, 30/10/2026).
    const categorieFiltree = sousRayonAffiche || rayonAffiche;
    if (categorieFiltree) {
      const allCategoryIds = getAllSubCategoryIds(categorieFiltree);
      filtered = filtered.filter((p) => allCategoryIds.includes(p.category_id || ''));
    }

    // Tri
    const getPrice = (p: Product) => {
      const priceEur = p.prices?.find((pr) => pr.currency === 'EUR');
      return priceEur ? priceEur.amount_cents : 0;
    };

    const searching = Boolean(searchQuery.trim());
    switch (sortBy) {
      case 'best':
        // Les plus vendus en caisse d'abord (rang sur 90 jours), puis le reste
        // par nom. En RECHERCHE, l'ordre par défaut reste la pertinence.
        if (!searching) {
          filtered.sort(
            (a, b) =>
              (a.sales_rank ?? Number.MAX_SAFE_INTEGER) - (b.sales_rank ?? Number.MAX_SAFE_INTEGER) ||
              a.name.localeCompare(b.name, 'fr'),
          );
        }
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name, 'fr'));
        break;
      case 'price-asc':
        filtered.sort((a, b) => getPrice(a) - getPrice(b));
        break;
      case 'price-desc':
        filtered.sort((a, b) => getPrice(b) - getPrice(a));
        break;
      case 'novelty':
        // Les annoncées d'abord, le reste dans son ordre. En RECHERCHE, on ne
        // touche à rien : l'ordre par défaut y signifie « par pertinence », et remonter
        // les nouveautés écraserait ce classement en silence.
        if (!searching) {
          filtered.sort((a, b) => Number(!!b.is_novelty) - Number(!!a.is_novelty));
        }
        break;
    }

    return filtered;
  }, [products, searchQuery, ownProductionOnly, promoOnly, noveltyOnly, favorisOnly, favoris, rayonAffiche, sousRayonAffiche, categories, sortBy]);

  // Reset displayCount when filters change
  useEffect(() => {
    setDisplayCount(TRANCHE_CATALOGUE);
  }, [searchQuery, ownProductionOnly, promoOnly, noveltyOnly, favorisOnly, selectedCategoryId, selectedSubCategoryId, sortBy]);

  // Produits affichés (paginés)
  const displayedProducts = useMemo(() => {
    return filteredProducts.slice(0, displayCount);
  }, [filteredProducts, displayCount]);

  // Chargement au défilement : dès que le bas de la liste entre dans le champ
  // (avec 600 px d'avance), la tranche suivante s'affiche. Le client parcourt
  // le catalogue d'un trait au lieu de cliquer tous les soixante produits.
  useEffect(() => {
    const cible = sentinelleRef.current;
    if (!cible || displayCount >= filteredProducts.length) return;
    if (typeof IntersectionObserver === 'undefined') return; // le bouton prend le relais

    const observateur = new IntersectionObserver(
      (entrees) => {
        if (entrees[0]?.isIntersecting) {
          setDisplayCount((prev) => Math.min(prev + TRANCHE_CATALOGUE, filteredProducts.length));
        }
      },
      { rootMargin: '600px' },
    );
    observateur.observe(cible);
    return () => observateur.disconnect();
  }, [displayCount, filteredProducts.length]);

  // Position dans la liste : notée au défilement, rendue UNE fois après le
  // chargement, et seulement pour la même vue (même rayon, même recherche).
  const vue = vueCourante(location.search, searchQuery);
  const vueRef = useRef(vue);
  vueRef.current = vue;
  const affichesRef = useRef(displayCount);
  affichesRef.current = displayCount;
  const positionRendue = useRef(false);
  useEffect(() => {
    if (loading) return;
    const zone = zoneProduitsRef.current;
    if (!zone) return;
    let attente = 0;
    const noter = () => {
      if (attente) return;
      attente = requestAnimationFrame(() => {
        attente = 0;
        positionListe.ecrire({ vue: vueRef.current, haut: zone.scrollTop, affiches: affichesRef.current });
      });
    };
    zone.addEventListener('scroll', noter, { passive: true });
    return () => {
      zone.removeEventListener('scroll', noter);
      if (attente) cancelAnimationFrame(attente);
    };
  }, [loading]);
  useEffect(() => {
    if (loading || positionRendue.current) return;
    positionRendue.current = true;
    const p = positionListe.lire();
    if (!p || p.vue !== vueRef.current || p.haut <= 0) return;
    setDisplayCount((n) => Math.max(n, p.affiches));
    // Deux images : laisser React afficher la tranche avant de défiler.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => zoneProduitsRef.current?.scrollTo({ top: p.haut })),
    );
  }, [loading]);

  // « Ajouté au panier » : la fiche se fermait sans rien dire, et sur un petit
  // écran le client ne savait pas si l'article était pris (cas Gailterie).
  const nbLignesPanier = cartItems.length;
  const lignesAvant = useRef(nbLignesPanier);
  const [confirmationAjout, setConfirmationAjout] = useState<number | null>(null);
  useEffect(() => {
    if (nbLignesPanier > lignesAvant.current) {
      setConfirmationAjout(nbLignesPanier);
      const t = setTimeout(() => setConfirmationAjout(null), 3500);
      lignesAvant.current = nbLignesPanier;
      return () => clearTimeout(t);
    }
    lignesAvant.current = nbLignesPanier;
  }, [nbLignesPanier]);

  // Titre de la vue courante (catégorie sélectionnée, recherche ou catalogue complet)
  const currentTitle = searchQuery
    ? `Recherche « ${searchQuery.trim()} »`
    : selectedSubCategoryId
      ? categories.find((c) => c.id === selectedSubCategoryId)?.name || 'Tous les produits'
      : selectedCategoryId
        ? categories.find((c) => c.id === selectedCategoryId)?.name || 'Tous les produits'
        : 'Tous les produits';

  // Bandeau « 100% bio » — affirme le bio une seule fois (pas de badge par produit)
  const bioBanner = (
    // Agrandi le 01/10/2026 (Philippe) : c'est l'engagement de la boutique, il
    // était écrit plus petit que le texte courant.
    <div className="bg-[#4a5c3a] text-[#f0e9d8] text-center px-3 py-2.5 sm:py-3 text-[16px] sm:text-[19px] leading-snug font-bold tracking-[.02em]">
      <span aria-hidden className="mr-1.5">🌱</span>
      <span className="text-[19px] sm:text-[23px] font-extrabold">100 %</span> de nos produits sont issus de l'agriculture biologique
    </div>
  );

  if (loading) {
    return (
      <div className="shop-sans min-h-screen bg-[#faf7f0] flex flex-col">
        <div className="flex-shrink-0 sticky top-0 z-30">
          <EcommerceHeader searchQuery={searchQuery} onSearchChange={setSearchQuery} />
          {bioBanner}
        </div>
        <div className="flex-1 p-4">
          {/* L'entrée (photo, présentation, horaires) ne dépend pas du catalogue :
              elle s'affiche tout de suite, les cases d'attente ne gardent la
              place que des produits (07/10/2026). */}
          {!accueilCache && !enRecherche && !rayonAffiche && (
            <AccueilBoutique onVoirProduits={() => undefined} onMasquer={() => setAccueilCache(true)} />
          )}
          <ProductSkeleton count={isMobile ? 6 : 10} />
        </div>
        {isMobile && <MobileBottomNav />}
      </div>
    );
  }

  return (
    // `relative` obligatoire : sur iPad (WebKit), les cartes produit sont
    // `position: relative` et, si ce conteneur `overflow-hidden` n'est pas lui-
    // même positionné, leur débordement compte dans la hauteur du DOCUMENT :
    // la page entière devenait défilable de 2 500 px de blanc, en-tête et
    // panier sortaient de l'écran (capture de Philippe, 29/08/2026). Mesuré :
    // scrollHeight 3 377 → 834 avec `relative`.
    <div className="shop-sans relative h-screen bg-[#faf7f0] flex flex-col overflow-hidden">
      {/* Offline banner */}
      <OfflineBanner />

      {/* Header + bandeau bio - Fixes en haut (sur ordinateur).
          Sur téléphone, SEULE l'en-tête reste fixe : horaires, bandeau bio et
          liens d'aide occupaient près de la moitié de l'écran sans jamais
          partir (capture du 28/09) — ils défilent avec la page (voir <main>). */}
      <div className="flex-shrink-0 sticky top-0 z-30">
        <EcommerceHeader searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        {/* L'encadré des horaires (« Commandez tous les jours — … reviennent
            mercredi… ») a été retiré le 28/09/2026 à la demande de Philippe : le
            schéma « Quand commander ? » de l'entrée le dit mieux, en dessin. */}
        {!isMobile && bioBanner}
      </div>

      {/* Comprendre, et lire autrement. Les deux liens vivaient dans le bandeau
          d'état, qui disparaît dès que la boutique prend commande : « Comment ça
          marche ? » n'était donc lisible QUE boutique fermée. Voir l'en-tête de
          BarreAideBoutique. */}
      {!isMobile && <BarreAideBoutique />}

      {/* Mobile category chips - visible only on mobile */}
      {isMobile && (
        <MobileCategoryChips
          categories={categories}
          selectedCategoryId={rayonAffiche}
          selectedSubCategoryId={sousRayonAffiche}
          onSelectCategory={choisirRayon}
          onSelectSubCategory={choisirSousRayon}
          productCounts={productCounts}
          ownProductionOnly={ownProductionOnly}
          onToggleOwnProduction={setOwnProductionOnly}
          ownProductionCount={ownProductionCount}
          promoOnly={promoOnly}
          onTogglePromo={setPromoOnly}
          promoCount={promoCount}
          noveltyOnly={noveltyOnly}
          onToggleNovelty={setNoveltyOnly}
          noveltyCount={noveltyCount}
          favorisOnly={favorisOnly}
          onToggleFavoris={favorisActifs ? setFavorisOnly : undefined}
          favorisCount={favorisCount}
          resultCount={filteredProducts.length}
        />
      )}

      {/* Badge flottant pour les nouvelles publications */}

      {/* Layout principal : Sidebar | Produits | Panier (desktop) / Produits only (mobile) */}
      <div className="flex-1 flex min-h-0">
        {/* Sidebar catégories - desktop only */}
        {!isMobile && (
          <CategorySidebar
            categories={categories}
            selectedCategoryId={rayonAffiche}
            selectedSubCategoryId={sousRayonAffiche}
            onSelectCategory={choisirRayon}
            onSelectSubCategory={choisirSousRayon}
            productCounts={productCounts}
            ownProductionOnly={ownProductionOnly}
            onToggleOwnProduction={setOwnProductionOnly}
            ownProductionCount={ownProductionCount}
            promoOnly={promoOnly}
            onTogglePromo={setPromoOnly}
            promoCount={promoCount}
            noveltyOnly={noveltyOnly}
            onToggleNovelty={setNoveltyOnly}
            noveltyCount={noveltyCount}
            favorisOnly={favorisOnly}
            onToggleFavoris={favorisActifs ? setFavorisOnly : undefined}
            favorisCount={favorisCount}
          />
        )}

        {/* Zone produits - centre - Défile indépendamment.
            <main> : c'est le repère qui permet à un lecteur d'écran de SAUTER
            l'en-tête, la recherche et le menu. Sans lui, la même en-tête était
            relue à chaque page — épuisant sur un catalogue (audit du
            15/09/2026 : aucun repère « contenu principal » sur les 4 pages). */}
        <main
          ref={zoneProduitsRef}
          className={`flex-1 overflow-y-auto bg-[#faf7f0] ${isMobile ? 'px-3 py-2 pb-40' : 'px-6 pt-6 pb-8'}`}
        >
          {/* Titre principal, lu mais non affiché : la page la plus importante du
              site n'avait AUCUN h1 parmi ses 57 titres (les noms de produits).
              Un lecteur d'écran liste les titres pour s'orienter — elle arrivait
              donc au catalogue sans savoir où elle était. Le rendre visible
              changerait la mise en page pour tout le monde ; le rendre lisible
              suffit. `absolute` hors écran plutôt que `display:none`, qui le
              retirerait aussi du lecteur. */}
          <h1 className="absolute w-px h-px -m-px overflow-hidden whitespace-nowrap border-0 p-0 [clip:rect(0,0,0,0)]">
            Catalogue des produits
          </h1>
          {isMobile && (
            <div className="-mx-3 -mt-2 mb-3">
              {bioBanner}
              <BarreAideBoutique />
            </div>
          )}
          {/* Entrée de la boutique : seulement sans recherche, rayon ni filtre. */}
          {!accueilCache && !enRecherche && !rayonAffiche && !ownProductionOnly && !promoOnly && !noveltyOnly && !favorisOnly && (
            <AccueilBoutique
              onVoirProduits={() => {
                // Faire défiler la SEULE zone des produits. `scrollIntoView`
                // fait aussi glisser les conteneurs en `overflow: hidden` (le
                // cadre plein écran de la page) : sur iPhone, en-tête et
                // bandeaux sortaient de l'écran et la liste s'arrêtait avant le
                // bas (capture du 28/09).
                const zone = zoneProduitsRef.current;
                const cible = debutGrilleRef.current;
                if (!zone || !cible) return;
                const ecart = cible.getBoundingClientRect().top - zone.getBoundingClientRect().top;
                zone.scrollTo({ top: zone.scrollTop + ecart - 8, behavior: 'smooth' });
              }}
              onMasquer={() => setAccueilCache(true)}
              rayonsFenetre={rayonsParRythme.fenetre}
              rayonsToujours={rayonsParRythme.toujours}
            />
          )}
          {/* Entrée masquée : un lien discret pour la rouvrir — sans lui, « Masquer »
              était sans retour pendant 30 jours (07/10/2026). */}
          {accueilCache && !enRecherche && !rayonAffiche && !ownProductionOnly && !promoOnly && !noveltyOnly && !favorisOnly && (
            <button
              type="button"
              onClick={() => { reafficherAccueil(); setAccueilCache(false); }}
              className="shop-sans mb-3 inline-flex min-h-[40px] items-center gap-2 rounded-full border border-[#4a5c3a]/25 bg-white px-4 text-[13.5px] font-semibold text-[#4a5c3a] active:bg-[#eef2e8]"
            >
              <span aria-hidden>🌱</span> Afficher la présentation de la boutique
            </button>
          )}
          {/* Fil d'Ariane - desktop only (masqué à la racine, le titre suffit) */}
          {!isMobile && rayonAffiche && (
            <nav className="flex items-center gap-1 text-[12.5px] text-[#6b6550] mb-3">
              <button
                onClick={() => {
                  setSelectedCategoryId(null);
                  setSelectedSubCategoryId(null);
                }}
                className="hover:text-[#b45f2e] hover:underline"
              >
                Tous les produits
              </button>
              {(() => {
                const rayon = categories.find((c) => c.id === selectedCategoryId);
                if (!rayon) return null;
                return (
                  <>
                    <ChevronRight className="w-3.5 h-3.5 text-[#6b6550]/70 flex-shrink-0" />
                    <button
                      onClick={() => {
                        setSelectedCategoryId(rayon.id);
                        setSelectedSubCategoryId(null);
                      }}
                      className={`hover:underline ${selectedSubCategoryId ? 'hover:text-[#b45f2e]' : 'text-[#4a4530] font-semibold'}`}
                    >
                      {rayon.name}
                    </button>
                  </>
                );
              })()}
              {selectedSubCategoryId && (() => {
                const subCat = categories.find((c) => c.id === selectedSubCategoryId);
                if (!subCat) return null;
                return (
                  <>
                    <ChevronRight className="w-3.5 h-3.5 text-[#6b6550]/70 flex-shrink-0" />
                    <span className="text-[#4a4530] font-semibold">{subCat.name}</span>
                  </>
                );
              })()}
            </nav>
          )}

          {/* Catalogue injoignable : le dire, et proposer de réessayer (le nouvel
              essai se fait aussi seul). « Aucun produit trouvé » serait faux. */}
          {catalogueEnEchec && (
            <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-[#4a4530] flex items-center justify-between gap-3 flex-wrap">
              <span>
                📡 <strong>Pas de connexion</strong> — {products.length > 0 ? 'les produits affichés peuvent ne pas être à jour.' : 'le catalogue n’a pas pu être chargé.'} Nouvel essai automatique dès que le réseau revient.
              </span>
              <button
                onClick={() => loadData(true)}
                disabled={nouvelEssai}
                className="px-4 py-2 rounded-lg bg-[#b45f2e] text-white font-semibold disabled:opacity-50"
              >
                {nouvelEssai ? 'Chargement…' : 'Réessayer'}
              </button>
            </div>
          )}

          {/* Message si aucun produit */}
          {filteredProducts.length === 0 && !catalogueEnEchec && (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">🔍</div>
              <p className="text-base text-[#4a4530] mb-1">Aucun produit trouvé</p>
              {searchQuery && (
                <p className="text-sm text-[#6b6550]">
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-[#b45f2e] hover:underline font-semibold"
                  >
                    Réinitialiser les filtres
                  </button>
                </p>
              )}
            </div>
          )}

          {/* Grille de produits */}
          {filteredProducts.length > 0 && (
            <>
              {/* En-tête de grille : titre catégorie + compteur + tri */}
              <div ref={debutGrilleRef} className="mb-4 flex items-baseline justify-between gap-2 flex-wrap">
                <div className={`shop-serif font-bold text-[#3a3324] ${isMobile ? 'text-lg' : 'text-[21px]'}`}>
                  {currentTitle}{' '}
                  <span className="shop-sans text-[13px] font-normal text-[#6b6550]">
                    — {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''}
                  </span>
                </div>
                <label className="text-[12.5px] text-[#6b6550] flex items-center gap-1.5">
                  Trier :
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="text-[12.5px] font-bold text-[#b45f2e] bg-transparent border-none cursor-pointer focus:outline-none"
                  >
                    <option value="best">Les plus vendus</option>
                    <option value="name-asc">Nom A-Z</option>
                    <option value="name-desc">Nom Z-A</option>
                    <option value="price-asc">Prix croissant</option>
                    <option value="price-desc">Prix décroissant</option>
                    {noveltyCount > 0 && <option value="novelty">Nouveautés d'abord</option>}
                  </select>
                </label>
              </div>
              {/* Barre des favoris : uniquement quand le filtre est actif. Elle
                  travaille sur les favoris REELLEMENT disponibles au catalogue
                  (`filteredProducts`), pas sur la liste brute — proposer
                  d'ajouter un article retire du catalogue ne menerait nulle part. */}
              {favorisOnly && (
                <BarreFavoris
                  produits={filteredProducts}
                  onToutAjouter={(aLaPiece) => {
                    aLaPiece.forEach((p) => addItem(p, { quantity: 1, addedBy: 'customer' }));
                  }}
                  onChoisirPoids={(p) => setSelectedProductId(p.id)}
                  connecte={favorisAuCompte}
                />
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {displayedProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onInfoClick={() => setSelectedProductId(product.id)}
                    estFavori={estFavori(product.id)}
                    onBasculerFavori={
                      favorisActifs ? () => void basculerFavori(product.id) : undefined
                    }
                  />
                ))}
              </div>
              {displayCount < filteredProducts.length && (
                <div className="flex justify-center mt-6 mb-4">
                  {/* Sentinelle : la tranche suivante se charge d'elle-même quand
                      le bas de la liste approche. Le bouton reste, pour qui
                      préfère cliquer et si l'observateur n'est pas disponible. */}
                  <div ref={sentinelleRef} aria-hidden="true" />
                  <button
                    onClick={() => setDisplayCount((prev) => prev + TRANCHE_CATALOGUE)}
                    className="px-6 py-2.5 bg-white border border-[#b45f2e] text-[#b45f2e] font-bold rounded-lg hover:bg-[#f0e9d8] transition-colors shadow-sm"
                  >
                    Charger plus ({filteredProducts.length - displayCount} restant{filteredProducts.length - displayCount > 1 ? 's' : ''})
                  </button>
                </div>
              )}
            </>
          )}

          {/* Coordonnées et TVA en bas de la page d'accueil. Ici, dans la zone
              qui défile : la mise en page du catalogue fait exactement la
              hauteur de l'écran, un pied de page posé en dehors serait hors
              d'atteinte. */}
          <EcommerceFooter />
        </main>

        {/* Panneau panier - colonne fixe 300px - Desktop only */}
        {!isMobile && (
          <div className="w-[300px] bg-[#fff8ea] border-l border-black/[.08] flex flex-col flex-shrink-0">
            {/* Panier - prend l'espace disponible avec scroll interne */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <ReceiptTicket />
            </div>

            {/* Live Shopping — désactivé temporairement */}
          </div>
        )}
      </div>

      {confirmationAjout !== null && (
        <div
          role="status"
          className="fixed left-1/2 -translate-x-1/2 bottom-[10.5rem] md:bottom-8 z-[45] px-4 py-2.5 rounded-full bg-[#4a5c3a] text-white text-sm font-semibold shadow-lg whitespace-nowrap"
        >
          ✓ Ajouté au panier — {confirmationAjout} article{confirmationAjout > 1 ? 's' : ''}
        </div>
      )}

      {/* Modal de détail produit */}
      {selectedProductId && (
        <ProductDetailModal
          productId={selectedProductId}
          onClose={() => setSelectedProductId(null)}
        />
      )}

      {/* Live Shopping — désactivé temporairement
      {showWaitingRoom && (
        <WaitingRoom
          onSessionStart={(sessionId) => {
            setShowWaitingRoom(false);
            setActiveSessionId(sessionId);
            setIsSessionHidden(false);
          }}
          onCancel={() => setShowWaitingRoom(false)}
        />
      )}

      {activeSessionId && !isSessionHidden && (
        <LiveVideoPanel
          sessionId={activeSessionId}
          onClose={() => setIsSessionHidden(true)}
          onEndSession={() => {
            setActiveSessionId(null);
            setIsSessionHidden(false);
            setVendorSessionEnded(null);
          }}
          joinSession={joinSession}
          messages={messages}
          sendMessage={sendMessage}
          syncCartState={syncCartState}
          cancelSession={cancelSession}
          isSessionConnected={isLiveConnected}
          getCancellationFees={getCancellationFees}
          cancelSessionWithFees={cancelSessionWithFees}
          vendorSessionEnded={vendorSessionEnded}
        />
      )}

      {activeSessionId && isSessionHidden && (
        <button
          onClick={() => setIsSessionHidden(false)}
          className={`fixed ${isMobile ? 'bottom-20' : 'bottom-4'} right-4 z-50 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 transition-all animate-pulse`}
        >
          <Video className="w-5 h-5" />
          <span className="font-medium">Rejoindre la session</span>
        </button>
      )}

      {showChatOnly && (
        <ChatOnlyPanel onClose={() => setShowChatOnly(false)} />
      )}
      */}

      {/* Mobile bottom navigation */}
      {isMobile && <MobileBottomNav />}

      {/* PWA install prompt */}
      <InstallPrompt />
    </div>
  );
}
